import {ChangeDetectionStrategy, Component} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Observable, combineLatest} from 'rxjs';
import {debounceTime, map, takeUntil} from 'rxjs/operators';
import {NavigationEventService} from '../../../add-ons/navigation-event.service';
import {OnDestroySubject} from '../../../data/model/on-destroy-subject';
import * as catalogSelector from '../../catalog/catalog.selectors';
import {selectTotalCatalogs} from '../../catalog/catalog.selectors';
import * as cartAction from '../../shopping-cart/cart.actions';
import {hideCartSlideOut} from '../../shopping-cart/cart.actions';
import * as cartSelector from '../../shopping-cart/cart.selectors';
import {EventEmitterService} from '../../event-emitter/event-emitter.service';
import {ActivatedRoute} from '@angular/router';
import {selectCustomerPaysFee} from '../../context-data/context-data.selectors';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  host: {class: 'app-layout'},
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayoutComponent extends OnDestroySubject {
  currentPage$: Observable<number>;
  totalGroups$: Observable<number>;
  showCart$: Observable<boolean>;
  currentRouteId$: Observable<string | undefined>;
  nextRouteId$: Observable<string | undefined>;
  previousRouteId$: Observable<string | undefined>;
  enableCart$: Observable<boolean>;
  cartSubtotal$: Observable<number>;
  cartItemCount$: Observable<number>;
  showUpfrontText$: Observable<boolean | undefined>;

  constructor(
    private store: Store,
    private navigationEventService: NavigationEventService,
    private eventEmitterService: EventEmitterService,
    private route: ActivatedRoute,
  ) {
    super();
    this.totalGroups$ = this.store.pipe(
      select(selectTotalCatalogs),
      takeUntil(this.onDestroy),
    );
    this.currentPage$ = this.store.pipe(
      select(catalogSelector.selectCurrentCatalogPageNumber),
      takeUntil(this.onDestroy),
    );
    this.showCart$ = this.store.pipe(
      select(cartSelector.selectShowCart),
      takeUntil(this.onDestroy),
    );
    this.nextRouteId$ = this.store.pipe(
      select(catalogSelector.selectNextRouteFragment),
      takeUntil(this.onDestroy),
    );
    this.previousRouteId$ = this.store.pipe(
      select(catalogSelector.selectPreviousRouteFragment),
      takeUntil(this.onDestroy),
    );
    this.cartSubtotal$ = this.store.pipe(
      select(cartSelector.selectCartSubtotalForDisplay),
      takeUntil(this.onDestroy),
    );
    this.cartItemCount$ = this.store.pipe(
      select(cartSelector.selectCartItemsForDisplay),
      map(items => items.length),
      takeUntil(this.onDestroy),
    );
    this.enableCart$ = this.store.pipe(
      select(cartSelector.selectEnableCart),
      takeUntil(this.onDestroy),
    );
    this.showUpfrontText$ = this.store.pipe(
      select(selectCustomerPaysFee),
      takeUntil(this.onDestroy),
    );
    this.subscribeEventEmitters();
  }

  continueClick(): void {
    this.navigationEventService.continueClick.emit();
    this.hideCart();
  }

  hideCart(): void {
    this.store.dispatch(hideCartSlideOut());
  }

  showCart(): void {
    this.store.dispatch(cartAction.showCartSlideOut());
  }

  private subscribeEventEmitters(): void {
    combineLatest([
      this.totalGroups$,
      this.currentPage$,
      this.nextRouteId$,
      this.previousRouteId$,
    ])
      .pipe(debounceTime(10), takeUntil(this.onDestroy))
      .subscribe(([totalPages, currentPage, nextRoute, previousRoute]) => {
        const currentRoute = [
          this.route.snapshot.url,
          ...this.route.snapshot.children[0].url,
        ].join('/');
        this.eventEmitterService.navigation({
          totalPages,
          currentPage,
          nextRoute,
          previousRoute,
          currentRoute,
        });
      });
  }
}
